import React from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/utils";
import { Camera, Film, Clapperboard, Lightbulb, Speaker, Video } from "lucide-react";

const IconNode = ({ icon: Icon, x, y, delay = 0, pulseColor = "#E8D1AB" }) => {
  return (
    <div 
      className="absolute z-20"
      style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay, duration: 0.5 }}
        className="relative flex items-center justify-center w-12 h-12 rounded-full bg-[#1D1D1B] border border-[#ECE1CE] shadow-[0_0_20px_rgba(236,225,206,0.2)]"
      >
        <Icon className="w-6 h-6 text-[#ECE1CE]" />
        
        {/* Glow ring */}
        <motion.div
          animate={{
            boxShadow: [
              `0 0 0 0px ${pulseColor}00`,
              `0 0 0 6px ${pulseColor}30`,
              `0 0 0 12px ${pulseColor}00`
            ],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            delay: delay + 0.5,
            ease: "easeInOut"
          }}
          className="absolute inset-0 rounded-full border border-[#E8D1AB]/30"
        />
      </motion.div>
    </div>
  );
};

export const PulseBeams = ({
  children,
  className,
  background,
  beams,
  width = 858,
  height = 434,
  baseColor = "#1D1D1B",
  accentColor = "#ECE1CE",
  gradientColors,
  icons = [] 
}) => {
  return (
    <div
      className={cn(
        "relative flex items-center justify-center antialiased",
        className
      )}
    >
      {background}
      <div className="relative z-10 w-full">{children}</div>
      
      <div className="absolute inset-0 pointer-events-none">
        {/* Icons Layer */}
        {icons.map((item, index) => (
           <IconNode 
             key={index} 
             icon={item.icon} 
             x={item.x} 
             y={item.y} 
             delay={item.delay} 
           />
        ))}
      
        {/* SVG Layer */}
        <div className="absolute inset-0 flex items-center justify-center opacity-60">
          <SVGs
            beams={beams}
            width={width}
            height={height}
            baseColor={baseColor}
            accentColor={accentColor}
            gradientColors={gradientColors}
          />
        </div>
      </div>
    </div>
  );
};

const SVGs = ({ beams, width, height, baseColor, accentColor, gradientColors }) => {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex flex-shrink-0 w-full h-full"
      preserveAspectRatio="none" // Ensure it stretches to fill the container which we control via aspect-ratio
    >
      {beams.map((beam, index) => (
        <React.Fragment key={index}>
          <path
            d={beam.path}
            stroke={baseColor}
            strokeWidth="2"
            strokeOpacity="0.3"
          />
          <path
            d={beam.path}
            stroke={`url(#grad${index})`}
            strokeWidth="3"
            strokeLinecap="round"
          />
          {beam.connectionPoints?.map((point, pointIndex) => (
            <circle
              key={`${index}-${pointIndex}`}
              cx={point.cx}
              cy={point.cy}
              r={point.r}
              fill={baseColor}
              stroke={accentColor}
              strokeWidth="2"
            />
          ))}
        </React.Fragment>
      ))}

      <defs>
        {beams.map((beam, index) => (
          <motion.linearGradient
            key={index}
            id={`grad${index}`}
            gradientUnits="userSpaceOnUse"
            initial={beam.gradientConfig.initial}
            animate={beam.gradientConfig.animate}
            transition={beam.gradientConfig.transition}
          >
            <GradientColors colors={gradientColors} />
          </motion.linearGradient>
        ))}
      </defs>
    </svg>
  );
};

const GradientColors = ({ colors = {
  start: "#ECE1CE",
  middle: "#D6C5A6",
  end: "#1D1D1B"
} }) => {
  return (
    <>
      <stop offset="0%" stopColor={colors.start} stopOpacity="0" />
      <stop offset="20%" stopColor={colors.start} stopOpacity="1" />
      <stop offset="50%" stopColor={colors.middle} stopOpacity="1" />
      <stop offset="100%" stopColor={colors.end} stopOpacity="0" />
    </>
  );
};

export default PulseBeams;
